import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Avatar,
  IconButton,
  Divider,
  Tooltip,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { tokens } from '../theme';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// Get initials from a label
const getInitials = (text) => {
  if (!text) return '?';
  const parts = String(text).trim().split(' ');
  if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return text.substring(0, 2).toUpperCase();
};

/**
 * QuizCard - Reusable card to display quiz information
 * Inspired by SystemDesign/PosteCard and SystemDesign/SkillCard
 *
 * Props:
 * - quiz: {
 *     quizId, title, description, timeLimit, maxAttempts,
 *     shuffleQuestions, shuffleOptions, isActive,
 *     createdAt, updatedAt, createdBy
 *   }
 * - index: for color rotation (optional)
 * - onView: action handler (optional)
 * - assignment: optional assignment object to render assignment meta
 *   {
 *     assignmentId, quizId, status, dueAt, expiresAt, assigner: { username },
 *   }
 * - onStart: optional handler to trigger quiz start (called with quizId)
 * - showUpdated: boolean (default true) to show/hide the Updated caption under title
 * - showCreator: boolean (default true) to show/hide the Created by footer
 * - assignmentActions: optional React node to render custom actions (e.g., Decline) next to Start
 */
const QuizCard = ({ quiz = {}, index = 0, onView, assignment = null, onStart, showUpdated = true, showCreator = true, assignmentActions = null }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const {
    title = 'Untitled Quiz',
    description = '',
    timeLimit = 0,
    maxAttempts = 1,
    shuffleQuestions = false,
    shuffleOptions = false,
    isActive = true,
    createdAt,
    updatedAt,
    createdBy = {},
    creator = {},
    questionsCount,
    skillsCount,
  } = quiz || {};

  const creatorUsername = creator?.username || createdBy?.username || createdBy?.name || creator?.name || '';

  // Color rotation similar to PosteCard
  const getCardColor = (idx) => {
    const colorOptions = [
      { bg: alpha(colors.primary[100], 0.1), border: colors.primary[200] },
      { bg: alpha(colors.Orange[200], 0.1), border: colors.Orange[200] },
      { bg: alpha(colors.green[200], 0.1), border: colors.green[200] },
      { bg: alpha(colors.Blue[200], 0.1), border: colors.Blue[200] },
      { bg: alpha(colors.Pink[400], 0.1), border: colors.Pink[400] },
      { bg: alpha(colors.purple[200], 0.1), border: colors.purple[200] },
    ];
    return colorOptions[idx % colorOptions.length];
  };
  const cardColor = getCardColor(index);

  const assignmentStatusColor = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'accepted') return { bg: 'rgba(46, 125, 50, 0.15)', color: '#2e7d32' };
    if (s === 'pending') return { bg: 'rgba(255, 167, 38, 0.15)', color: '#f57c00' };
    if (s === 'revoked') return { bg: 'rgba(211, 47, 47, 0.15)', color: '#d32f2f' };
    if (s === 'declined') return { bg: 'rgba(211, 47, 47, 0.15)', color: '#d32f2f' };
    return { bg: 'rgba(120, 144, 156, 0.15)', color: '#546e7a' };
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
  };

  return (
    <Card
      sx={{
        backgroundColor: cardColor.bg,
        borderColor: cardColor.border,
        borderWidth: 2,
        borderStyle: 'solid',
        borderRadius: '16px',
        transition: 'all 0.2s',
        height: '100%',
        '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' },
      }}
    >
      <CardContent>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            size="small"
            icon={isActive ? <CheckCircleIcon sx={{ color: colors.green[400] }} /> : <CancelIcon sx={{ color: colors.redAccent[400] }} />}
            sx={{
              bgcolor: isActive ? alpha(colors.green[400], 0.15) : alpha(colors.redAccent[400], 0.15),
              color: isActive ? colors.green[500] : colors.redAccent[500],
              fontWeight: 500,
            }}
          />

          {/* Action icons */}
          <Box display="flex" alignItems="center" gap={0.5}>
            {onView && (
              <Tooltip title="View quiz">
                <IconButton size="small" onClick={() => onView(quiz)}>
                  <VisibilityIcon sx={{ color: colors.grey[650] }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Title */}
        <Box display="flex" alignItems="flex-start" gap={2} mb={1.5}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'background.paper',
              color: 'text.primary',
              fontWeight: 'bold',
              fontSize: '1.2rem',
              boxShadow: 1,
              border: '2px solid white',
            }}
          >
            {getInitials(title)}
          </Avatar>
          <Box>
            <Typography variant="h6" component="h3" fontWeight="bold" sx={{
              mb: 0.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {title}
            </Typography>
            {updatedAt && showUpdated && (
              <Typography variant="caption" color="text.secondary">
                Updated: {new Date(updatedAt).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Meta badges */}
        <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
          <Chip
            icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
            label={`${timeLimit} min`}
            size="small"
          />
          <Chip
            icon={<AutorenewIcon sx={{ fontSize: 18 }} />}
            label={`${maxAttempts} attempt(s)`}
            size="small"
          />
          {Number.isFinite(questionsCount) && (
            <Chip
              label={`${questionsCount} question(s)`}
              size="small"
            />
          )}
          {Number.isFinite(skillsCount) && (
            <Chip
              label={`${skillsCount} skill(s)`}
              size="small"
            />
          )}
          {shuffleQuestions && (
            <Chip
              icon={<ShuffleIcon sx={{ fontSize: 18 }} />}
              label="Shuffle questions"
              size="small"
              sx={{ bgcolor: alpha(colors.primary[300], 0.15), color: colors.primary[400] }}
            />
          )}
          {shuffleOptions && (
            <Chip
              icon={<ShuffleIcon sx={{ fontSize: 18 }} />}
              label="Shuffle options"
              size="small"
              sx={{ bgcolor: alpha(colors.Orange[400], 0.15), color: colors.Orange[400] }}
            />
          )}
        </Box>

        {/* Description */}
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{
            color: colors.grey[700],
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minHeight: '3.6em',
          }}>
            {description || 'No description provided'}
          </Typography>
        </Box>

        {(showCreator || assignment) && <Divider sx={{ my: 1 }} />}

        {/* Footer */}
        {showCreator && (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Created by
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar sx={{ width: 24, height: 24, fontSize: '0.625rem', bgcolor: 'grey.600', color: 'white' }}>
                {getInitials(creatorUsername)}
              </Avatar>
              <Typography variant="body2" fontWeight="medium">
                {creatorUsername || 'Unknown'}
              </Typography>
            </Box>
          </Box>
        )}

        {assignment && (
          <>
            <Box display="grid" gridTemplateColumns="1fr" gap={1}>
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Chip size="small" label={`Assigned by: ${assignment?.assigner?.username || assignment?.assignedBy || 'Unknown'}`} />
                {!!assignment?.status && (
                  (() => {
                    const { bg, color } = assignmentStatusColor(assignment.status);
                    return <Chip size="small" label={String(assignment.status).replace(/_/g, ' ')} sx={{ bgcolor: bg, color }} />;
                  })()
                )}
              </Box>
              <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Typography variant="caption" color="text.secondary">Due: {fmtDate(assignment?.dueAt)}</Typography>
                <Typography variant="caption" color="text.secondary">Expires: {fmtDate(assignment?.expiresAt)}</Typography>
              </Box>
              {(assignmentActions || onStart) && (
                <Box display="flex" justifyContent="flex-end" mt={0.5}>
                  <Box display="flex" gap={1}>
                    {assignmentActions}
                    {onStart && (
                      <Button size="small" variant="contained" onClick={() => onStart(quiz?.quizId)}>
                        Start
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default QuizCard;
